(function () {
  "use strict";

  const ROOT_SELECTOR = '[data-brk-tool="sizing-simulator"]';
  const CONFIG_ID = "brk-tool-sizing-config";
  const NODE_ID = "N_L2T_TOOL_SIZING_SIMULATOR";

  const STATES = {
    SUITABLE: {
      title: "Within Barklin sizing",
      text: "The selected inputs are inside the Barklin sizing system."
    },
    SUITABLE_WITH_CAUTION: {
      title: "Usable with fit checks",
      text: "The result can guide the choice, but fit position, coat or profile needs closer manual checking."
    },
    RESTRICTED_CONDITIONAL_USE: {
      title: "Use with manual check",
      text: "This profile needs a separate fit-position check. Use the result as orientation only."
    },
    NOT_IDEAL_FOR_BARKLIN_SCOPE: {
      title: "Outside clear Barklin sizing",
      text: "The selected inputs are outside the calculator’s clear sizing logic."
    }
  };

  const REASONS = {
    reason_m_defaults_wide: "Medium neck ranges usually default to 4 cm in the Barklin sizing system.",
    reason_m_slim_exception: "This M result uses the slim exception because no wide-width modifier was detected.",
    reason_long_neck: "Long slim neck profiles usually need more careful adjustment.",
    reason_strong_neck: "Strong broad neck profiles usually default toward the wider support surface.",
    reason_thick_coat: "Dense coat can affect how the collar settles on the neck.",
    reason_regular_fit_checks: "Regular fit checks are required for this result.",
    reason_escape_sensitive: "Unstable collar position is outside the normal Barklin sizing scope.",
    reason_outside_range: "The entered measurement is outside the supported 25–65 cm range.",
    reason_restricted_sighthound_use: "Sighthound profiles need a separate collar-position check.",
    reason_short_sensitive_caution: "Short sensitive neck profiles require a separate fit-position check.",
    reason_strong_pull_medium_wide: "Pulling behaviour in the M range keeps the result on the wider setup.",
    reason_brachycephalic: "Short or compressed neck profiles need separate fit assessment. For leash walking, handling method should be checked separately.",
    reason_toy_scope_boundary: "Very small neck measurements may sit near or outside the lower Barklin sizing boundary.",
    reason_puppy_transition: "Puppies and young dogs need repeated measurement during the transition phase.",
    reason_giant_scope_boundary: "Very large neck measurements may sit near or outside the upper Barklin sizing boundary.",
    reason_modelled_reference: "This breed row includes modelled orientation data. The measured neck circumference remains primary."
  };

  const FIT_INTENSITY = {
    normal: "Normal",
    medium: "Medium",
    high: "High"
  };

  const ALLOWED_ENUMS = {
    use_case: ["city_walk", "social_walk", "cafe_walk", "training", "active"],
    leash_behaviour: ["calm", "some_pull", "strong_pull", "escape"],
    fitcheck_routine: ["regular", "occasional", "unsure"],
    collar_purpose: ["everyday", "id_only", "style", "control"],
    neck_profile: [
      "standard_neck",
      "long_slim_neck",
      "strong_broad_neck",
      "thick_coated_neck",
      "sighthound_neck",
      "short_sensitive_neck"
    ]
  };

  const STATE_ICONS = {
    SUITABLE:
      '<svg width="16" height="16" viewBox="0 0 16 16" aria-hidden="true"><path d="M3.5 8.4l3 3 6-6" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>',
    SUITABLE_WITH_CAUTION:
      '<svg width="16" height="16" viewBox="0 0 16 16" aria-hidden="true"><path d="M8 3v6" stroke="currentColor" stroke-width="2" stroke-linecap="round"/><circle cx="8" cy="12" r="1.1" fill="currentColor"/></svg>',
    RESTRICTED_CONDITIONAL_USE:
      '<svg width="16" height="16" viewBox="0 0 16 16" aria-hidden="true"><circle cx="8" cy="8" r="5.6" fill="none" stroke="currentColor" stroke-width="1.6"/><path d="M4 12L12 4" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/></svg>',
    NOT_IDEAL_FOR_BARKLIN_SCOPE:
      '<svg width="16" height="16" viewBox="0 0 16 16" aria-hidden="true"><path d="M4 4l8 8M12 4l-8 8" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>'
  };

  function readConfig() {
    const node = document.getElementById(CONFIG_ID);
    if (!node) return null;

    try {
      const config = JSON.parse(node.textContent || "{}");

      if (config.node_id !== NODE_ID) {
        return null;
      }

      return config;
    } catch (error) {
      return null;
    }
  }

  function normalizeArray(value) {
    if (Array.isArray(value)) return value;

    if (typeof value === "string" && value.trim()) {
      try {
        const parsed = JSON.parse(value);
        return Array.isArray(parsed) ? parsed : [];
      } catch (error) {
        return value
          .split(",")
          .map((item) => item.trim())
          .filter(Boolean);
      }
    }

    return [];
  }

  function normalizeObject(value) {
    if (value && typeof value === "object" && !Array.isArray(value)) return value;

    if (typeof value === "string" && value.trim()) {
      try {
        const parsed = JSON.parse(value);
        return parsed && typeof parsed === "object" && !Array.isArray(parsed) ? parsed : {};
      } catch (error) {
        return {};
      }
    }

    return {};
  }

  function safeEnum(value, enumName, fallback) {
    const allowed = ALLOWED_ENUMS[enumName] || [];
    return allowed.includes(value) ? value : fallback;
  }

  function getLanguage(locale) {
    return String(locale || "en").toLowerCase().slice(0, 2);
  }

  function pickLocalized(breed, baseKey, locale) {
    if (!breed || !baseKey) return "";

    const lang = getLanguage(locale);

    return (
      breed[`${baseKey}_${lang}`] ||
      breed[`${baseKey}_en`] ||
      breed[`${baseKey}_de`] ||
      breed[baseKey] ||
      ""
    );
  }

  function getBreedNote(breed, locale) {
    if (!breed) return "";

    const lang = getLanguage(locale);

    if (lang === "de") {
      return breed.db_note_de || breed.db_note_en || breed.db_note || "";
    }

    if (lang === "hu") {
      return breed.db_note_hu || breed.db_note_en || breed.db_note || "";
    }

    if (lang === "ro") {
      return breed.db_note_ro || breed.db_note_en || breed.db_note || "";
    }

    return breed.db_note_en || breed.db_note || "";
  }

  function parseNeckCm(value) {
    return Number(String(value || "").trim().replace(",", "."));
  }

  function resolveBand(neckCm) {
    if (neckCm < 25 || neckCm > 65) return null;
    if (neckCm < 35) return "S";
    if (neckCm < 45) return "M";
    if (neckCm < 55) return "L";
    return "XL";
  }

  function resolveBreedProfile(breed) {
    if (!breed) return {};

    return {
      breed_key: breed.breed_key || "",
      neck_profile: breed.neck_profile || "",
      cluster_group: breed.cluster_group || "",
      width_bias: breed.width_bias || "",
      suitability_bias: breed.suitability_bias || "",
      fit_check_intensity: breed.fit_check_intensity || "",
      is_sighthound: Boolean(breed.is_sighthound),
      is_brachycephalic: Boolean(breed.is_brachycephalic),
      tracheal_risk: Boolean(breed.tracheal_risk),
      escape_risk: String(breed.escape_risk || "").toUpperCase(),
      coat_adds_cm: Number(breed.coat_adds_cm || 0),
      size_category: breed.size_category || "",
      source_status: breed.source_status || ""
    };
  }

  function hasWideModifier(context) {
    return (
      context.profile === "long_slim_neck" ||
      context.profile === "strong_broad_neck" ||
      context.profile === "thick_coated_neck" ||
      context.profile === "sighthound_neck" ||
      context.thickFur ||
      context.leashBehaviour === "some_pull" ||
      context.leashBehaviour === "strong_pull" ||
      context.breed.width_bias === "wide" ||
      context.breed.cluster_group === "broad_neck" ||
      context.breed.cluster_group === "windhund_sighthound" ||
      context.breed.is_sighthound
    );
  }

  function resolveWidth(neckCm, band, context) {
    const reasons = [];

    if (!band) {
      return {
        widthCm: "",
        sizeVariant: "",
        reasons: []
      };
    }

    let widthCm = "4 cm";
    let sizeVariant = band;
    let wideScore = 0;

    if (band === "S") {
      return {
        widthCm: "2.5 cm",
        sizeVariant: "S",
        reasons
      };
    }

    if (band === "L" || band === "XL") {
      return {
        widthCm: "4 cm",
        sizeVariant: band,
        reasons
      };
    }

    if (context.profile === "long_slim_neck") {
      wideScore += 1;
      reasons.push("reason_long_neck");
    }

    if (context.profile === "strong_broad_neck") {
      wideScore += 1;
      reasons.push("reason_strong_neck");
    }

    if (context.profile === "thick_coated_neck" || context.thickFur) {
      wideScore += 1;
      reasons.push("reason_thick_coat");
    }

    if (context.leashBehaviour === "strong_pull") {
      wideScore += 1;
      reasons.push("reason_strong_pull_medium_wide");
    }

    const slimException =
      neckCm >= 35 &&
      neckCm <= 38.5 &&
      !hasWideModifier(context) &&
      wideScore <= 0;

    if (slimException) {
      widthCm = "2.5 cm";
      sizeVariant = "M_SLIM";
      reasons.push("reason_m_slim_exception");
    } else {
      widthCm = "4 cm";
      sizeVariant = "M_WIDE";
      reasons.push("reason_m_defaults_wide");
    }

    return {
      widthCm,
      sizeVariant,
      reasons
    };
  }

  function resolveSuitability(neckCm, band, context) {
    const reasons = [];
    let state = "SUITABLE";
    let fitIntensity = "normal";

    if (!band) {
      let outsideReason = "reason_outside_range";

      if (Number.isFinite(neckCm)) {
        if (neckCm < 25) outsideReason = "reason_toy_scope_boundary";
        else if (neckCm > 65) outsideReason = "reason_giant_scope_boundary";
      }

      return {
        state: "NOT_IDEAL_FOR_BARKLIN_SCOPE",
        fitIntensity: "high",
        reasons: [outsideReason]
      };
    }

    const isSighthound =
      context.profile === "sighthound_neck" ||
      context.breed.is_sighthound ||
      context.breed.cluster_group === "windhund_sighthound";

    const isShortSensitive = context.profile === "short_sensitive_neck";
    const isBrachycephalic = context.breed.is_brachycephalic || context.breed.tracheal_risk;

    if (context.breed.size_category === "very_small" || context.breed.cluster_group === "toy_small") {
      state = "NOT_IDEAL_FOR_BARKLIN_SCOPE";
      fitIntensity = "high";
      reasons.push("reason_toy_scope_boundary");
    }

    if (isSighthound && context.leashBehaviour === "escape") {
      return {
        state: "NOT_IDEAL_FOR_BARKLIN_SCOPE",
        fitIntensity: "high",
        reasons: ["reason_escape_sensitive", "reason_restricted_sighthound_use"]
      };
    }

    if (
      isBrachycephalic &&
      (context.collarPurpose === "everyday" || context.collarPurpose === "control")
    ) {
      return {
        state: "NOT_IDEAL_FOR_BARKLIN_SCOPE",
        fitIntensity: "high",
        reasons: ["reason_brachycephalic", "reason_short_sensitive_caution"]
      };
    }

    const calmUrbanUse =
      context.useCase === "city_walk" ||
      context.useCase === "social_walk" ||
      context.useCase === "cafe_walk";

    if (isSighthound && calmUrbanUse && context.fitcheckRoutine === "regular") {
      state = "RESTRICTED_CONDITIONAL_USE";
      fitIntensity = "high";
      reasons.push("reason_restricted_sighthound_use", "reason_regular_fit_checks");
    }

    if (
      isShortSensitive &&
      (context.collarPurpose === "id_only" || context.collarPurpose === "style") &&
      context.leashBehaviour === "calm" &&
      context.fitcheckRoutine === "regular" &&
      calmUrbanUse &&
      state !== "NOT_IDEAL_FOR_BARKLIN_SCOPE"
    ) {
      state = "SUITABLE_WITH_CAUTION";
      fitIntensity = "medium";
      reasons.push("reason_short_sensitive_caution", "reason_regular_fit_checks");
    }

    const cautionSignals = [
      context.thickFur,
      context.profile === "long_slim_neck",
      context.profile === "strong_broad_neck",
      context.profile === "thick_coated_neck",
      context.leashBehaviour === "some_pull",
      context.leashBehaviour === "strong_pull",
      context.fitcheckRoutine !== "regular",
      band === "M" && hasWideModifier(context),
      context.breed.suitability_bias === "caution",
      context.breed.fit_check_intensity === "medium"
    ];

    if (context.breed.suitability_bias === "restricted") {
      state = "RESTRICTED_CONDITIONAL_USE";
      fitIntensity = "high";
    }

    if (context.breed.suitability_bias === "manual" && state !== "NOT_IDEAL_FOR_BARKLIN_SCOPE") {
      state = "SUITABLE_WITH_CAUTION";
      fitIntensity = "high";
    }

    if (state === "SUITABLE" && cautionSignals.some(Boolean)) {
      state = "SUITABLE_WITH_CAUTION";
      fitIntensity = context.leashBehaviour === "strong_pull" ? "high" : "medium";
    }

    if (context.profile === "long_slim_neck") reasons.push("reason_long_neck");
    if (context.profile === "strong_broad_neck") reasons.push("reason_strong_neck");
    if (context.profile === "thick_coated_neck" || context.thickFur) reasons.push("reason_thick_coat");
    if (isShortSensitive) reasons.push("reason_short_sensitive_caution");
    if (isBrachycephalic) reasons.push("reason_brachycephalic");
    if (isSighthound) reasons.push("reason_restricted_sighthound_use");

    if (context.fitcheckRoutine === "regular" && state !== "SUITABLE") {
      reasons.push("reason_regular_fit_checks");
    }

    if (
      context.breed.source_status &&
      /model|estimate|estimated|modelled/i.test(context.breed.source_status)
    ) {
      reasons.push("reason_modelled_reference");
    }

    return {
      state,
      fitIntensity,
      reasons: Array.from(new Set(reasons))
    };
  }

  function calculate(input) {
    const neckCm = parseNeckCm(input.neckCm);
    const band = resolveBand(neckCm);
    const breed = resolveBreedProfile(input.breed);

    const resolvedProfile = safeEnum(
      input.neckProfile || breed.neck_profile,
      "neck_profile",
      "standard_neck"
    );

    const context = {
      breed,
      profile: resolvedProfile,
      thickFur: Boolean(input.thickFur || breed.coat_adds_cm > 0),
      useCase: safeEnum(input.useCase, "use_case", "city_walk"),
      leashBehaviour: safeEnum(input.leashBehaviour, "leash_behaviour", "calm"),
      fitcheckRoutine: safeEnum(input.fitcheckRoutine, "fitcheck_routine", "regular"),
      collarPurpose: safeEnum(input.collarPurpose, "collar_purpose", "everyday")
    };

    const width = resolveWidth(neckCm, band, context);
    const suitability = resolveSuitability(neckCm, band, context);

    return {
      band,
      sizeVariant: width.sizeVariant,
      widthCm: width.widthCm,
      state: suitability.state,
      fitIntensity: suitability.fitIntensity,
      profile: context.profile,
      breed: input.breed || null,
      reasons: Array.from(new Set([].concat(width.reasons, suitability.reasons)))
    };
  }

  function getStateTitle(key, customStates, labelStates) {
    const custom = customStates[key];

    if (typeof custom === "string") return custom;
    if (custom && typeof custom === "object" && custom.title) return custom.title;

    return labelStates[key] || (STATES[key] && STATES[key].title) || key;
  }

  function getStateDescription(key, customStates, labelStateText) {
    const custom = customStates[key];

    if (custom && typeof custom === "object" && custom.text) return custom.text;

    return labelStateText[key] || (STATES[key] && STATES[key].text) || "";
  }

  function getReasonText(key, customReasons, labelReasons) {
    const custom = customReasons[key];

    if (typeof custom === "string") return custom;
    if (custom && typeof custom === "object" && custom.text) return custom.text;

    return labelReasons[key] || REASONS[key] || key;
  }

  function localizedWidth(widthToken, widthLabels) {
    if (widthToken === "2.5 cm") return widthLabels.narrow || "2.5 cm";
    if (widthToken === "4 cm") return widthLabels.wide || "4 cm";
    return widthToken || "";
  }

  function setText(node, value) {
    if (node) node.textContent = value || "";
  }

  function setHidden(node, hidden) {
    if (node) node.hidden = Boolean(hidden);
  }

  function setStateIcon(iconNode, stateKey) {
    if (!iconNode) return;
    iconNode.innerHTML = STATE_ICONS[stateKey] || "";
  }

  function groupKeyForBreed(breed) {
    const cat = String(breed.size_category || "").toLowerCase();
    const cluster = String(breed.cluster_group || "").toLowerCase();

    if (cat === "very_small" || cat === "small" || cat === "toy" || cluster === "toy_small") {
      return "toy_small";
    }

    if (cat === "medium") return "medium";
    if (cat === "large") return "large";
    if (cat === "giant" || cat === "very_large") return "giant";

    return "other";
  }

  function populateBreeds(select, breeds, config) {
    if (!select || !Array.isArray(breeds)) return;

    const labels = normalizeObject(config.labels);
    const optgroupLabels = normalizeObject(labels.breed_optgroups);
    const messages = normalizeObject(config.messages);

    const curatedKeys = normalizeArray(config.curated_keys);
    const curatedSet = new Set(curatedKeys);

    const visibleBreeds = breeds
      .filter((breed) => breed && breed.breed_key)
      .filter((breed) => breed.tool_visible !== false)
      .filter((breed) => curatedSet.size === 0 || curatedSet.has(breed.breed_key))
      .sort((a, b) => {
        const nameA = pickLocalized(a, "breed_name", config.locale) || a.breed_key;
        const nameB = pickLocalized(b, "breed_name", config.locale) || b.breed_key;
        return nameA.localeCompare(nameB);
      });

    select.innerHTML = "";

    const placeholderOption = document.createElement("option");
    placeholderOption.value = "";
    placeholderOption.textContent = messages.breed_placeholder || "Select breed";
    select.appendChild(placeholderOption);

    const groups = {
      toy_small: [],
      medium: [],
      large: [],
      giant: [],
      other: []
    };

    visibleBreeds.forEach((breed) => {
      const key = groupKeyForBreed(breed);
      groups[key].push(breed);
    });

    ["toy_small", "medium", "large", "giant", "other"].forEach((groupKey) => {
      const items = groups[groupKey];
      if (!items.length) return;

      const optgroup = document.createElement("optgroup");
      optgroup.label = optgroupLabels[groupKey] || groupKey;

      items.forEach((breed) => {
        const option = document.createElement("option");
        option.value = breed.breed_key;
        option.textContent = pickLocalized(breed, "breed_name", config.locale) || breed.breed_key;
        optgroup.appendChild(option);
      });

      select.appendChild(optgroup);
    });

    select.disabled = false;
  }

  function findBreed(breeds, key) {
    if (!key || !Array.isArray(breeds)) return null;
    return breeds.find((breed) => breed && breed.breed_key === key) || null;
  }

  async function loadDataset(url) {
    if (!url) return [];

    const response = await fetch(url, {
      credentials: "same-origin",
      headers: {
        Accept: "application/json"
      },
      cache: "no-store"
    });

    if (!response.ok) {
      throw new Error("Dataset request failed.");
    }

    const data = await response.json();

    if (Array.isArray(data)) return data;
    if (Array.isArray(data.breeds)) return data.breeds;

    return [];
  }

  function scrollResultIntoView(resultNode) {
    if (!resultNode) return;

    const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const isMobile = window.matchMedia("(max-width: 749px)").matches;

    const rect = resultNode.getBoundingClientRect();
    const isClearlyVisible = rect.top >= 80 && rect.bottom <= window.innerHeight - 24;

    if (!isMobile && isClearlyVisible) return;

    const offset = isMobile ? 76 : 96;
    const targetY = window.scrollY + rect.top - offset;

    window.requestAnimationFrame(function () {
      window.scrollTo({
        top: Math.max(0, targetY),
        behavior: reduceMotion ? "auto" : "smooth"
      });

      window.setTimeout(function () {
        try {
          resultNode.focus({ preventScroll: true });
        } catch (error) {
          resultNode.focus();
        }
      }, reduceMotion ? 0 : 320);
    });
  }

  function setupJumpCTA(resultNode, jumpCtaNode) {
    if (!resultNode || !jumpCtaNode) return null;

    let showTimer = null;
    let observer = null;

    function isInView() {
      const rect = resultNode.getBoundingClientRect();
      return rect.top < window.innerHeight - 80 && rect.bottom > 80;
    }

    function show() {
      if (!window.matchMedia("(max-width: 749px)").matches) return;

      if (showTimer) window.clearTimeout(showTimer);

      showTimer = window.setTimeout(function () {
        if (!isInView()) {
          setHidden(jumpCtaNode, false);
        }
      }, 650);
    }

    function hide() {
      if (showTimer) {
        window.clearTimeout(showTimer);
        showTimer = null;
      }

      setHidden(jumpCtaNode, true);
    }

    jumpCtaNode.addEventListener("click", function (event) {
      event.preventDefault();
      hide();
      scrollResultIntoView(resultNode);
    });

    if ("IntersectionObserver" in window) {
      observer = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            if (entry.isIntersecting && entry.intersectionRatio >= 0.3) {
              hide();
            }
          });
        },
        { threshold: [0.3] }
      );

      observer.observe(resultNode);
    }

    return { show, hide, observer };
  }

  function revealResult(resultNode, jumpCTA) {
    setHidden(resultNode, false);

    if (!resultNode) return;

    resultNode.setAttribute("data-result-updated", "true");

    window.setTimeout(function () {
      resultNode.removeAttribute("data-result-updated");
    }, 1600);

    scrollResultIntoView(resultNode);

    if (jumpCTA) jumpCTA.show();
  }

  function init(root) {
    const config = readConfig();
    if (!config) return false;

    const form = root.querySelector("[data-brk-sizing-form]");
    const calculateButton = root.querySelector("[data-brk-sizing-calculate]");
    const neck = root.querySelector("[data-brk-sizing-neck]");
    const neckError = root.querySelector("[data-brk-sizing-neck-error]");
    const breedSelect = root.querySelector("[data-brk-sizing-breed]");
    const profile = root.querySelector("[data-brk-sizing-profile]");
    const thickFur = root.querySelector("[data-brk-sizing-thick-fur]");
    const useCase = root.querySelector("[data-brk-sizing-use-case]");
    const leash = root.querySelector("[data-brk-sizing-leash]");
    const fitcheck = root.querySelector("[data-brk-sizing-fitcheck]");
    const purpose = root.querySelector("[data-brk-sizing-purpose]");
    const warning = root.querySelector("[data-brk-sizing-dataset-warning]");
    const actionFeedback = root.querySelector("[data-brk-sizing-action-feedback]");

    const result = root.querySelector("[data-brk-sizing-result]");
    const stateNode = root.querySelector("[data-brk-sizing-state]");
    const stateIconNode = root.querySelector("[data-brk-sizing-state-icon]");
    const stateTextNode = root.querySelector("[data-brk-sizing-state-text]");
    const sizeNode = root.querySelector("[data-brk-sizing-size]");
    const widthNode = root.querySelector("[data-brk-sizing-width]");
    const profileNode = root.querySelector("[data-brk-sizing-profile-result]");
    const fitNode = root.querySelector("[data-brk-sizing-fit-intensity]");
    const breedResultNode = root.querySelector("[data-brk-sizing-breed-result]");
    const reasonsNode = root.querySelector("[data-brk-sizing-reasons]");

    const exitWrapper = root.querySelector("[data-brk-tool-result-exit]");
    const exit25 = root.querySelector("[data-brk-sizing-exit-25]");
    const exit40 = root.querySelector("[data-brk-sizing-exit-40]");

    const jumpCtaNode = root.querySelector("[data-brk-sizing-jump-cta]");

    if (!form || !calculateButton || !neck || !result) {
      return false;
    }

    const messages = normalizeObject(config.messages);
    const customStates = normalizeObject(config.states);
    const customReasons = normalizeObject(config.reasons);
    const widthLabels = normalizeObject(config.widths);

    const labels = normalizeObject(config.labels);
    const labelStates = normalizeObject(labels.states);
    const labelStateText = normalizeObject(labels.state_text);
    const labelReasons = normalizeObject(labels.reasons);
    const labelProfiles = normalizeObject(labels.profiles);
    const labelSizes = normalizeObject(labels.sizes);
    const labelFit = normalizeObject(labels.fit);

    const jumpCTA = setupJumpCTA(result, jumpCtaNode);

    let breeds = [];

    loadDataset(config.dataset_url)
      .then(function (loadedBreeds) {
        breeds = loadedBreeds;
        populateBreeds(breedSelect, breeds, config);
      })
      .catch(function () {
        if (breedSelect) {
          breedSelect.disabled = true;

          const placeholder = breedSelect.querySelector("option");
          if (placeholder) {
            placeholder.textContent = messages.breed_placeholder || "Select breed";
          }
        }

        if (warning) {
          warning.textContent = messages.dataset_warning || "Breed refinement is temporarily unavailable.";
          setHidden(warning, false);
        }
      });

    function updateExitLinks(output) {
      if (!exitWrapper) return;

      const show25 = output && output.widthCm === "2.5 cm";
      const show40 = output && output.widthCm === "4 cm";

      if (!show25 && !show40) {
        setHidden(exitWrapper, true);
        if (exit25) setHidden(exit25, true);
        if (exit40) setHidden(exit40, true);
        return;
      }

      setHidden(exitWrapper, false);

      if (exit25) setHidden(exit25, !show25);
      if (exit40) setHidden(exit40, !show40);
    }

    if (neck) {
      neck.addEventListener("input", function () {
        const raw = String(neck.value || "").trim();

        if (raw === "") {
          neck.removeAttribute("aria-invalid");
          setHidden(neckError, true);
          return;
        }

        const num = parseNeckCm(raw);

        if (!Number.isFinite(num) || num < 25 || num > 65) {
          neck.setAttribute("aria-invalid", "true");
        } else {
          neck.removeAttribute("aria-invalid");
          setHidden(neckError, true);
        }
      });
    }

    function runCalculation(event) {
      if (event) event.preventDefault();

      const neckValue = neck ? String(neck.value || "").trim() : "";
      const neckCm = parseNeckCm(neckValue);
      const isValidNumber = neckValue !== "" && Number.isFinite(neckCm);

      if (!isValidNumber) {
        if (neckError) {
          neckError.textContent = messages.use_numbers_only || "Use numbers only.";
          setHidden(neckError, false);
        }

        if (neck) neck.setAttribute("aria-invalid", "true");

        setHidden(result, true);
        updateExitLinks(null);

        if (jumpCTA) jumpCTA.hide();

        return;
      }

      if (neckCm < 25 || neckCm > 65) {
        if (neckError) {
          neckError.textContent =
            messages.range_error || "Enter a realistic neck measurement between 25 and 65 cm.";
          setHidden(neckError, false);
        }

        if (neck) neck.setAttribute("aria-invalid", "true");
      } else {
        setHidden(neckError, true);
        if (neck) neck.removeAttribute("aria-invalid");
      }

      const selectedBreed = findBreed(breeds, breedSelect && breedSelect.value);

      const output = calculate({
        neckCm,
        breed: selectedBreed,
        neckProfile: profile && profile.value,
        thickFur: thickFur && thickFur.checked,
        useCase: useCase && useCase.value,
        leashBehaviour: leash && leash.value,
        fitcheckRoutine: fitcheck && fitcheck.value,
        collarPurpose: purpose && purpose.value
      });

      if (result) {
        result.setAttribute("data-state", output.state || "");
      }

      setStateIcon(stateIconNode, output.state);

      setText(stateNode, getStateTitle(output.state, customStates, labelStates));
      setText(stateTextNode, getStateDescription(output.state, customStates, labelStateText));

      setText(sizeNode, labelSizes[output.sizeVariant] || output.sizeVariant || "—");
      setText(widthNode, localizedWidth(output.widthCm, widthLabels) || "—");
      setText(profileNode, labelProfiles[output.profile] || output.profile || "—");

      setText(
        fitNode,
        labelFit[output.fitIntensity] ||
          FIT_INTENSITY[output.fitIntensity] ||
          output.fitIntensity ||
          "—"
      );

      if (breedResultNode) {
        const breedName = selectedBreed
          ? pickLocalized(selectedBreed, "breed_name", config.locale) || selectedBreed.breed_key
          : "";

        const breedNote = selectedBreed ? getBreedNote(selectedBreed, config.locale) : "";

        setText(
          breedResultNode,
          breedName && breedNote ? `${breedName} — ${breedNote}` : breedName || "—"
        );
      }

      if (reasonsNode) {
        reasonsNode.innerHTML = "";

        output.reasons.forEach(function (reasonKey) {
          const item = document.createElement("li");
          item.textContent = getReasonText(reasonKey, customReasons, labelReasons);
          reasonsNode.appendChild(item);
        });
      }

      updateExitLinks(output);
      revealResult(result, jumpCTA);
    }

    form.addEventListener("submit", runCalculation);
    calculateButton.addEventListener("click", runCalculation);

    form.addEventListener("reset", function () {
      window.setTimeout(function () {
        setHidden(result, true);
        setHidden(neckError, true);
        setHidden(actionFeedback, true);

        updateExitLinks(null);

        if (jumpCTA) jumpCTA.hide();

        if (neck) neck.removeAttribute("aria-invalid");

        if (result) {
          result.removeAttribute("data-state");
          result.removeAttribute("data-result-updated");
        }

        if (reasonsNode) {
          reasonsNode.innerHTML = "";
        }
      }, 0);
    });

    updateExitLinks(null);

    return true;
  }

  function boot() {
    const root = document.querySelector(ROOT_SELECTOR);
    if (!root) return false;

    if (root.dataset.brkSizingReady === "true") return true;

    const didInit = init(root);

    if (didInit) {
      root.dataset.brkSizingReady = "true";
      return true;
    }

    return false;
  }

  function bootWithRetry() {
    if (boot()) return;

    let tries = 0;
    const maxTries = 40;

    const timer = window.setInterval(function () {
      tries += 1;

      if (boot() || tries >= maxTries) {
        window.clearInterval(timer);
      }
    }, 100);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", bootWithRetry, { once: true });
  } else {
    bootWithRetry();
  }

  window.addEventListener("load", bootWithRetry);
  window.addEventListener("pageshow", bootWithRetry);

  document.addEventListener("shopify:section:load", bootWithRetry);
  document.addEventListener("shopify:section:reorder", bootWithRetry);
})();